import clsx from 'clsx';

export interface GradientTileSequenceProps {
  count: number;
  activeIndex?: number;
  className?: string;
  /** px height of each tile. Default 48. */
  tileHeight?: number;
  /** Optional labels rendered below each tile. */
  labels?: string[];
}

/**
 * Horizontal sequence of gradient-shaded rounded tiles — the NEXA
 * "Entry Sequence" element. Deep blue at the start fading to blue-whisper at
 * the end. `activeIndex` fills a tile solid-ink for emphasis.
 */
export function GradientTileSequence({
  count,
  activeIndex,
  className,
  tileHeight = 48,
  labels,
}: GradientTileSequenceProps) {
  const tiles = Array.from({ length: count });
  return (
    <div className={clsx('w-full', className)}>
      <div className="flex items-stretch gap-1.5 w-full">
        {tiles.map((_, i) => {
          const t = count === 1 ? 0 : i / (count - 1);
          // Interpolate between blue-deep (#1D4ED8) at t=0 and blue-whisper (#DBEAFE) at t=1
          const color = interpolateBlue(t);
          const isActive = activeIndex === i;
          return (
            <div
              key={i}
              className={clsx(
                'flex-1 rounded-md transition-all duration-300',
                isActive && 'ring-2 ring-ink ring-offset-2 ring-offset-canvas',
              )}
              style={{
                height: tileHeight,
                backgroundColor: isActive ? '#0B0D12' : color,
              }}
            />
          );
        })}
      </div>
      {labels ? (
        <div className="mt-3 flex items-stretch gap-1.5 w-full">
          {labels.map((label, i) => (
            <div key={i} className="flex-1 min-w-0">
              <span className="font-mono text-[10px] uppercase tracking-[0.125em] text-ink-subtle block truncate">
                {label}
              </span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function interpolateBlue(t: number): string {
  // From #1D4ED8 (29,78,216) to #DBEAFE (219,234,254)
  const r = Math.round(29 + (219 - 29) * t);
  const g = Math.round(78 + (234 - 78) * t);
  const b = Math.round(216 + (254 - 216) * t);
  return `rgb(${r}, ${g}, ${b})`;
}
